/**
 * Tailwind class strings for the console's own chrome, written against the
 * canonical `--nim-*` tokens.
 *
 * These exist as constants rather than `.console-*` selectors because
 * `src/styles.css` no longer carries component rules and Preflight is on.
 * Preflight collapses every border to `0` and strips padding off form controls,
 * so each constant states those explicitly rather than inheriting them from the
 * user agent — which is also why they were already written this way in Phase 4,
 * before Preflight was turned on.
 *
 * Started under `organization/` and moved up when auth, requests and feedback
 * wanted the same buttons. Anything reachable from more than one mode belongs
 * here; a one-surface style stays at its call site.
 */

export const CARD =
  'rounded-[0.9rem] border border-nim bg-nim p-5 shadow-[var(--console-shadow)]';

export const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-[0.55rem] border-0 '
  + 'text-[0.85rem] font-medium leading-none whitespace-nowrap cursor-pointer '
  + 'transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-45';

export const BUTTON_PRIMARY =
  `${BUTTON_BASE} min-h-[2.25rem] px-3.5 text-[var(--nim-on-primary)] bg-nim-primary `
  + 'enabled:hover:bg-nim-primary-hover';

export const BUTTON_SECONDARY =
  `${BUTTON_BASE} min-h-[2.25rem] px-3 border border-nim text-nim bg-nim-secondary `
  + 'enabled:hover:border-nim-focus enabled:hover:text-[var(--nim-primary)]';

export const BUTTON_GHOST =
  `${BUTTON_BASE} h-7 px-2 text-nim-muted bg-transparent enabled:hover:bg-nim-hover enabled:hover:text-nim`;

export const BUTTON_GHOST_DANGER =
  `${BUTTON_BASE} h-7 px-2 text-[var(--nim-error)] bg-transparent `
  + 'enabled:hover:bg-[color-mix(in_srgb,var(--nim-error)_14%,transparent)]';

export const FIELD =
  'w-full min-w-0 rounded border border-nim bg-nim px-2.5 py-1.5 text-[13px] text-nim '
  + 'outline-none transition-colors duration-150 placeholder:text-nim-faint '
  + 'focus:border-[var(--nim-primary)] disabled:opacity-50';

export const MENU_SURFACE =
  'z-50 min-w-[11rem] overflow-auto rounded-lg border border-nim bg-nim p-1 '
  + 'shadow-[var(--console-shadow-lg)]';

export const MENU_ITEM =
  'flex w-full items-center gap-2 rounded border-0 bg-transparent px-2 py-1.5 text-left '
  + 'text-[13px] text-nim cursor-pointer transition-colors duration-150 hover:bg-nim-hover '
  + 'focus:bg-nim-hover focus:outline-none';

export const FIELD_LABEL =
  'block text-[0.66rem] font-semibold uppercase tracking-[0.1em] text-nim-muted';

export const SECTION_TITLE = 'm-0 text-[0.95rem] font-semibold text-nim';

export const SECTION_SUBTITLE = 'm-0 text-[0.8rem] leading-relaxed text-nim-muted';

/** Pill used for a role or an invitation state. */
export const PILL =
  'inline-flex items-center rounded-full px-2 py-0.5 text-[0.68rem] font-medium capitalize whitespace-nowrap';

/**
 * The taller button the auth pages and the standalone panels use, where the
 * control is the page's one action rather than a row's affordance.
 */
export const BUTTON_PRIMARY_LG =
  `${BUTTON_BASE} min-h-[2.5rem] px-4 border border-transparent text-[var(--nim-on-primary)] bg-nim-primary `
  + 'enabled:hover:bg-nim-primary-hover no-underline';

export const BUTTON_SECONDARY_LG =
  `${BUTTON_BASE} min-h-[2.5rem] px-4 border border-nim text-nim bg-nim-secondary `
  + 'enabled:hover:border-nim-focus enabled:hover:text-[var(--nim-primary)] no-underline';

export const BUTTON_DANGER =
  `${BUTTON_BASE} min-h-[2.5rem] px-4 border border-transparent text-[var(--nim-on-primary)] `
  + 'bg-[var(--nim-error)] enabled:hover:brightness-110';

/**
 * A word that acts. Reads as prose until hovered, which is what keeps a
 * destructive row action from looking like a button you were meant to press.
 */
export const BUTTON_TEXT =
  'border-0 bg-transparent text-[0.78rem] font-medium text-nim-muted cursor-pointer '
  + 'enabled:hover:text-nim disabled:cursor-not-allowed disabled:opacity-45';

export const BUTTON_TEXT_DANGER =
  'border-0 bg-transparent text-[0.78rem] font-medium text-[var(--nim-error)] cursor-pointer '
  + 'enabled:hover:underline disabled:cursor-not-allowed disabled:opacity-45';

/** Inline banners. Both are bordered and tinted from one semantic token. */
export const ALERT_BASE = 'rounded-[0.6rem] border px-4 py-3 text-[0.85rem] leading-normal';
export const ALERT_ERROR =
  `${ALERT_BASE} border-[var(--nim-error)] text-[var(--nim-error)] `
  + 'bg-[color-mix(in_srgb,var(--nim-error)_12%,transparent)]';
export const ALERT_SUCCESS =
  `${ALERT_BASE} border-[var(--nim-success)] text-[var(--nim-success)] `
  + 'bg-[color-mix(in_srgb,var(--nim-success)_12%,transparent)]';

/**
 * The eyebrow above a page heading, and the step label on the sign-in card.
 * Same treatment, two names in the copy, so one constant.
 */
export const EYEBROW =
  'text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-[var(--nim-primary)]';

/**
 * The full-page frame the auth callback, the no-access refusal and the
 * not-found route all share. A page rather than a mode surface: it has no
 * gutter beside it, so it carries its own background wash and centres its card.
 */
export const CALLBACK_SCREEN =
  'callback-screen grid min-h-screen place-items-center p-8 text-nim-muted '
  + 'bg-[radial-gradient(circle_at_50%_0,var(--nim-bg-selected),transparent_34rem),var(--nim-bg)]';

export const CALLBACK_CARD = 'callback-card w-[min(34rem,100%)] text-center';

/** A wide form field: the login card, the danger zone, the seat count. */
export const FIELD_WIDE =
  'w-full min-w-0 rounded-lg border border-nim bg-nim px-3 py-2.5 text-nim outline-none '
  + 'transition-colors duration-150 placeholder:text-nim-muted placeholder:opacity-70 '
  + 'focus:border-nim-focus focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--nim-border-focus)_35%,transparent)]';
